import { useEffect, useState } from "react";
import { LoginPage } from "./LoginPage";
import { AdminBoardPage } from "./AdminBoardPage";
import { fetchSession, phaseAuthHeaders, signOut, type SessionUser } from "./auth-client";
import { ProraAssistant, type AssistantContext } from "./ProraAssistant";
import { useTheme } from "./theme";

type HealthSnapshot = {
  ok: boolean;
  service: string;
  version: string;
  ts?: string;
  financial_core?: {
    status: "ok" | "degraded" | "unreachable";
    url?: string;
    detail?: unknown;
  };
};

type CalcResult = {
  indicator: string;
  value: number | null;
  threshold: number;
  semaforo: string;
  regola_applicata: string;
  flag_dati: string[];
};

// ------------------------------------------------------------
// Fixture clienti dimostrativi
// ------------------------------------------------------------

type ClienteDemoBilancio = {
  totale_attivo: number;
  patrimonio_netto: number;
  ricavi: number;
  ebitda: number;
  debiti_finanziari: number;
  risultato_esercizio: number;
};

type ClienteDemo = {
  id: string;
  ragione_sociale: string;
  settore_descrittivo: string;
  dipendenti: number;
  esercizio: string;
  forma_giuridica: string;
  bilancio: ClienteDemoBilancio;
  payload: {
    schema_version: string;
    stato_patrimoniale: { totale_attivo: number; patrimonio_netto: number };
  };
};

const CLIENTI_DEMO: ClienteDemo[] = [
  {
    id: "pmi-sana",
    ragione_sociale: "Esempio Pmi Sana S.r.l.",
    settore_descrittivo: "Commercio al dettaglio",
    dipendenti: 3,
    esercizio: "2024",
    forma_giuridica: "S.r.l.",
    bilancio: { totale_attivo: 850000, patrimonio_netto: 320000, ricavi: 1420000, ebitda: 165000, debiti_finanziari: 240000, risultato_esercizio: 55000 },
    payload: { schema_version: "1.0", stato_patrimoniale: { totale_attivo: 850000, patrimonio_netto: 320000 } },
  },
  {
    id: "tensione-moderata",
    ragione_sociale: "Esempio Tensione Moderata S.r.l.",
    settore_descrittivo: "Lavorazioni meccaniche",
    dipendenti: 6,
    esercizio: "2024",
    forma_giuridica: "S.r.l.",
    bilancio: { totale_attivo: 1600000, patrimonio_netto: 195000, ricavi: 2080000, ebitda: 165000, debiti_finanziari: 820000, risultato_esercizio: 7000 },
    payload: { schema_version: "1.0", stato_patrimoniale: { totale_attivo: 1600000, patrimonio_netto: 195000 } },
  },
  {
    id: "pn-negativo",
    ragione_sociale: "Esempio Pn Negativo S.r.l.",
    settore_descrittivo: "Ristorazione",
    dipendenti: 4,
    esercizio: "2024",
    forma_giuridica: "S.r.l.",
    bilancio: { totale_attivo: 720000, patrimonio_netto: -85000, ricavi: 860000, ebitda: -40000, debiti_finanziari: 475000, risultato_esercizio: -137000 },
    payload: { schema_version: "1.0", stato_patrimoniale: { totale_attivo: 720000, patrimonio_netto: -85000 } },
  },
];

type BilancioDraft = {
  ragione_sociale: string;
  settore_descrittivo: string;
  dipendenti: string;
  esercizio: string;
  forma_giuridica: string;
  totale_attivo: string;
  patrimonio_netto: string;
  ricavi: string;
  ebitda: string;
  debiti_finanziari: string;
  risultato_esercizio: string;
};

type UploadedBalanceApi = {
  id: string;
  ragioneSociale: string;
  settoreDescrittivo: string;
  dipendenti: number;
  esercizio: string;
  formaGiuridica: string;
  totaleAttivo: number;
  patrimonioNetto: number;
  ricavi: number;
  ebitda: number;
  debitiFinanziari: number;
  risultatoEsercizio: number;
  sourceName?: string | null;
  createdAt?: string;
};

const EMPTY_BILANCIO_DRAFT: BilancioDraft = {
  ragione_sociale: "",
  settore_descrittivo: "",
  dipendenti: "",
  esercizio: "2024",
  forma_giuridica: "S.r.l.",
  totale_attivo: "",
  patrimonio_netto: "",
  ricavi: "",
  ebitda: "",
  debiti_finanziari: "",
  risultato_esercizio: "",
};

// ------------------------------------------------------------
// Utility
// ------------------------------------------------------------

const eur = new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });

function parseAmount(raw: string): number {
  const normalized = raw
    .trim()
    .replace(/\s/g, "")
    .replace(/[€]/g, "")
    .replace(/\./g, "")
    .replace(",", ".");
  const value = Number(normalized);
  if (!Number.isFinite(value)) throw new Error(`Valore non valido: ${raw || "vuoto"}`);
  return value;
}

function clienteFromDraft(draft: BilancioDraft): ClienteDemo {
  const totale_attivo = parseAmount(draft.totale_attivo);
  const patrimonio_netto = parseAmount(draft.patrimonio_netto);
  const ricavi = parseAmount(draft.ricavi);
  const ebitda = parseAmount(draft.ebitda);
  const debiti_finanziari = parseAmount(draft.debiti_finanziari);
  const risultato_esercizio = parseAmount(draft.risultato_esercizio);
  const dipendenti = Number.parseInt(draft.dipendenti || "0", 10);

  if (!draft.ragione_sociale.trim()) throw new Error("Inserisci la ragione sociale.");
  if (!Number.isFinite(dipendenti) || dipendenti < 0) throw new Error("Dipendenti non valido.");
  if (totale_attivo <= 0) throw new Error("Totale attivo deve essere maggiore di zero.");

  return {
    id: `upload-${Date.now()}`,
    ragione_sociale: draft.ragione_sociale.trim(),
    settore_descrittivo: draft.settore_descrittivo.trim() || "Settore non indicato",
    dipendenti,
    esercizio: draft.esercizio.trim() || "2024",
    forma_giuridica: draft.forma_giuridica.trim() || "S.r.l.",
    bilancio: { totale_attivo, patrimonio_netto, ricavi, ebitda, debiti_finanziari, risultato_esercizio },
    payload: { schema_version: "1.0", stato_patrimoniale: { totale_attivo, patrimonio_netto } },
  };
}

function clienteFromUploadedBalance(item: UploadedBalanceApi): ClienteDemo {
  return {
    id: `saved-${item.id}`,
    ragione_sociale: item.ragioneSociale,
    settore_descrittivo: item.settoreDescrittivo,
    dipendenti: item.dipendenti,
    esercizio: item.esercizio,
    forma_giuridica: item.formaGiuridica,
    bilancio: {
      totale_attivo: item.totaleAttivo,
      patrimonio_netto: item.patrimonioNetto,
      ricavi: item.ricavi,
      ebitda: item.ebitda,
      debiti_finanziari: item.debitiFinanziari,
      risultato_esercizio: item.risultatoEsercizio,
    },
    payload: {
      schema_version: "1.0",
      stato_patrimoniale: { totale_attivo: item.totaleAttivo, patrimonio_netto: item.patrimonioNetto },
    },
  };
}

function balancePayloadFromCliente(cliente: ClienteDemo, sourceName?: string | null) {
  return {
    ragioneSociale: cliente.ragione_sociale,
    settoreDescrittivo: cliente.settore_descrittivo,
    dipendenti: cliente.dipendenti,
    esercizio: cliente.esercizio,
    formaGiuridica: cliente.forma_giuridica,
    totaleAttivo: cliente.bilancio.totale_attivo,
    patrimonioNetto: cliente.bilancio.patrimonio_netto,
    ricavi: cliente.bilancio.ricavi,
    ebitda: cliente.bilancio.ebitda,
    debitiFinanziari: cliente.bilancio.debiti_finanziari,
    risultatoEsercizio: cliente.bilancio.risultato_esercizio,
    sourceName: sourceName ?? null,
  };
}

function draftFromJson(value: unknown): BilancioDraft {
  if (!value || typeof value !== "object") throw new Error("JSON non valido.");
  const obj = value as Record<string, unknown>;
  const bilancio = (obj.bilancio && typeof obj.bilancio === "object" ? obj.bilancio : obj) as Record<string, unknown>;
  const get = (key: string) => (bilancio[key] ?? obj[key] ?? "").toString();

  return {
    ragione_sociale: (obj.ragione_sociale ?? obj.nome ?? "").toString(),
    settore_descrittivo: (obj.settore_descrittivo ?? obj.settore ?? "").toString(),
    dipendenti: (obj.dipendenti ?? "").toString(),
    esercizio: (obj.esercizio ?? "2024").toString(),
    forma_giuridica: (obj.forma_giuridica ?? "S.r.l.").toString(),
    totale_attivo: get("totale_attivo"),
    patrimonio_netto: get("patrimonio_netto"),
    ricavi: get("ricavi"),
    ebitda: get("ebitda"),
    debiti_finanziari: get("debiti_finanziari"),
    risultato_esercizio: get("risultato_esercizio"),
  };
}

function semaforoStyle(s: string): { box: string; label: string } {
  switch (s) {
    case "VERDE":
      return {
        box: "bg-emerald-50 border-emerald-300 dark:bg-emerald-950/40 dark:border-emerald-700",
        label: "text-emerald-900 dark:text-emerald-200",
      };
    case "GIALLO":
      return {
        box: "bg-amber-50 border-amber-300 dark:bg-amber-950/40 dark:border-amber-700",
        label: "text-amber-900 dark:text-amber-200",
      };
    case "ARANCIO":
      return {
        box: "bg-orange-50 border-orange-300 dark:bg-orange-950/40 dark:border-orange-700",
        label: "text-orange-900 dark:text-orange-200",
      };
    case "ROSSO":
      return {
        box: "bg-rose-50 border-rose-300 dark:bg-rose-950/40 dark:border-rose-700",
        label: "text-rose-900 dark:text-rose-200",
      };
    case "NON_CALCOLABILE":
    default:
      return {
        box: "bg-slate-100 border-slate-300 dark:bg-slate-800/50 dark:border-slate-700",
        label: "text-slate-700 dark:text-slate-300",
      };
  }
}

function letturaInterpretativa(cliente: ClienteDemo, r: CalcResult): string {
  if (r.value === null) {
    return `Per ${cliente.ragione_sociale} l'indice di adeguatezza patrimoniale non è calcolabile dai dati di bilancio forniti. Il professionista verifica l'integrità delle voci di Stato Patrimoniale prima di procedere alla valutazione.`;
  }
  const base = `L'indice di adeguatezza patrimoniale di ${cliente.ragione_sociale} per l'esercizio ${cliente.esercizio} si attesta a ${r.value.toFixed(4)} (riferimento CNDCEC: ${r.threshold.toFixed(2)}).`;
  switch (r.semaforo) {
    case "VERDE":
      return `${base} Il valore è sopra la soglia di riferimento: la struttura patrimoniale risulta adeguata alla natura e alla dimensione dell'impresa. Il professionista, ai sensi dell'art. 2086 c.c., conferma la solidità della copertura del totale attivo da parte dei mezzi propri.`;
    case "GIALLO":
      return `${base} Il valore è in fascia di attenzione tecnica. Il professionista valuta la dinamica nel triennio e l'eventuale necessità di interventi di rafforzamento patrimoniale (ricapitalizzazione, postergazione di finanziamenti soci, riduzione distribuzioni).`;
    case "ROSSO":
      if (r.flag_dati.includes("patrimonio netto negativo")) {
        return `${base} Il patrimonio netto risulta negativo: l'impresa è in stato di sottocapitalizzazione strutturale ai sensi dell'art. 2447 / 2482-ter c.c. Il professionista verifica gli adempimenti civilistici dell'organo amministrativo e l'eventuale percorribilità di un piano di risanamento ai sensi del CCII.`;
      }
      return `${base} Il valore è in fascia critica. Il professionista valuta urgentemente la perseguibilità di un piano di rafforzamento patrimoniale o, in alternativa, l'attivazione degli strumenti di Composizione Negoziata previsti dal CCII.`;
    default:
      return base;
  }
}

function letturaInterpretativaIndebitamento(cliente: ClienteDemo, r: CalcResult): string {
  if (r.value === null) {
    if (r.flag_dati.includes("patrimonio netto non positivo")) {
      return `Per ${cliente.ragione_sociale} l'indice di indebitamento finanziario non è valutabile come rapporto: il patrimonio netto risulta non positivo. La condizione configura sottocapitalizzazione strutturale (artt. 2447 / 2482-ter c.c.) e va trattata come valutazione qualitativa diretta, prevalente sulla lettura del ratio.`;
    }
    return `Per ${cliente.ragione_sociale} l'indice di indebitamento finanziario non è disponibile dai dati di bilancio forniti.`;
  }
  const base = `L'indice di indebitamento finanziario di ${cliente.ragione_sociale} per l'esercizio ${cliente.esercizio} si attesta a ${r.value.toFixed(4)} (debiti finanziari per ogni euro di mezzi propri).`;
  switch (r.semaforo) {
    case "VERDE":
      if (r.flag_dati.includes("nessun indebitamento finanziario rilevato")) {
        return `${base} L'impresa risulta priva di indebitamento finanziario significativo: la struttura è autofinanziata. Il professionista verifica che la voce sia effettivamente assente in bilancio (e non frutto di errore di classificazione).`;
      }
      return `${base} Il valore è in fascia di equilibrio: il debito finanziario è coperto dai mezzi propri. Struttura compatibile con un'autonomia finanziaria adeguata.`;
    case "GIALLO":
      return `${base} La leva finanziaria è moderata. Il professionista valuta la composizione tra esposizioni a breve e medio-lungo termine, il costo medio del debito e la redditività operativa (EBITDA) come elementi di qualificazione.`;
    case "ARANCIO":
      return `${base} La leva finanziaria è elevata: l'impresa è significativamente dipendente da finanziamenti esterni. Il professionista valuta la sostenibilità dinamica del debito (DSCR D.M. 21/03/2023) e la capacità di rifinanziamento delle posizioni a scadenza.`;
    case "ROSSO":
      return `${base} La leva finanziaria appare strutturalmente insostenibile. Il professionista valuta l'attivazione degli strumenti del CCII (Composizione Negoziata o procedure di regolazione) come opzione di tutela preventiva.`;
    default:
      return base;
  }
}

// ------------------------------------------------------------
// Theme toggle button
// ------------------------------------------------------------

export function ThemeToggle() {
  const [theme, toggle] = useTheme();
  const isDark = theme === "dark";
  return (
    <button
      onClick={toggle}
      title={isDark ? "passa al tema chiaro" : "passa al tema scuro"}
      aria-label="toggle tema"
      className="text-sm w-9 h-9 rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex items-center justify-center"
    >
      {isDark ? "☀" : "☾"}
    </button>
  );
}

// ------------------------------------------------------------
// Dropdown "Altri esempi" — report PRORA dimostrativi (PDF statici)
// ------------------------------------------------------------
//
// Documenti di esempio convalidati, serviti come asset statici da public/esempi/.
// NON sono output della pipeline attuale: servono in demo per mostrare il
// formato-target dei report PRORA più complessi.

type EsempioReport = { file: string; titolo: string; nota: string };

const ESEMPI_REPORT: EsempioReport[] = [
  {
    file: "/esempi/quadro-di-cassa-genesi.pdf",
    titolo: "Quadro di Cassa — Genesi S.r.l.",
    nota: "Proiezione flussi di cassa a 12 settimane, tre scenari",
  },
  {
    file: "/esempi/rapporto-ccii-geco.pdf",
    titolo: "Rapporto CCII — G.ECO",
    nota: "Analisi degli indicatori CCII su bilancio annuale",
  },
  {
    file: "/esempi/foglio-cliente-barcentrale.pdf",
    titolo: "Foglio Cliente — Bar Centrale",
    nota: "Sintesi di valutazione, caso standard",
  },
  {
    file: "/esempi/foglio-cliente-barcentrale-critico.pdf",
    titolo: "Foglio Cliente — Bar Centrale (scenario critico)",
    nota: "Sintesi di valutazione, situazione di tensione",
  },
];

export function EsempiDropdown() {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative min-w-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="min-h-10 w-full whitespace-nowrap text-sm px-3 py-1.5 border border-slate-300 dark:border-slate-700 rounded text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
      >
        Altri esempi ▾
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} aria-hidden />
          <div className="fixed left-3 right-3 top-36 z-40 max-h-[60vh] overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg p-1 sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-1 sm:w-80">
            <div className="px-3 py-2 text-xs text-slate-600 dark:text-slate-400 border-b border-slate-100 dark:border-slate-800">
              Report PRORA di esempio — documenti dimostrativi, non generati dalla pipeline attuale
            </div>
            {ESEMPI_REPORT.map((e) => (
              <a
                key={e.file}
                href={e.file}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className="block px-3 py-2 rounded hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                <div className="text-sm font-medium text-slate-900 dark:text-slate-100">{e.titolo}</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">{e.nota}</div>
              </a>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ------------------------------------------------------------
// HomePage
// ------------------------------------------------------------

function HomePage({ user, onSignOut }: { user: SessionUser; onSignOut: () => Promise<void> }) {
  const [health, setHealth] = useState<HealthSnapshot | null>(null);
  const [result, setResult] = useState<CalcResult | null>(null);
  const [resultError, setResultError] = useState<string | null>(null);
  const [resultIndebitamento, setResultIndebitamento] = useState<CalcResult | null>(null);
  const [resultIndebitamentoError, setResultIndebitamentoError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [clienteId, setClienteId] = useState<string>(CLIENTI_DEMO[0].id);
  const [customCliente, setCustomCliente] = useState<ClienteDemo | null>(null);
  const [savedClienti, setSavedClienti] = useState<ClienteDemo[]>([]);
  const [uploaderOpen, setUploaderOpen] = useState(false);
  const [bilancioDraft, setBilancioDraft] = useState<BilancioDraft>(EMPTY_BILANCIO_DRAFT);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSourceName, setUploadSourceName] = useState<string | null>(null);
  const [showImproveTab, setShowImproveTab] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [assistantContext, setAssistantContext] = useState<AssistantContext | null>(null);

  const clienti = customCliente ? [...CLIENTI_DEMO, ...savedClienti, customCliente] : [...CLIENTI_DEMO, ...savedClienti];
  const cliente = clienti.find((c) => c.id === clienteId) ?? CLIENTI_DEMO[0];

  const checkHealth = async () => {
    try {
      const r = await fetch("/health");
      setHealth((await r.json()) as HealthSnapshot);
    } catch { setHealth(null); }
  };

  const runCalc = async (c: ClienteDemo) => {
    setResultError(null); setResult(null);
    setResultIndebitamentoError(null); setResultIndebitamento(null);
    setLoading(true);
    try {
      const [adeguatezzaRes, indebitamentoRes] = await Promise.all([
        fetch("/api/calc/adeguatezza-patrimoniale", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(c.payload),
        }),
        fetch("/api/calc/indebitamento-finanziario", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            debiti_finanziari: c.bilancio.debiti_finanziari,
            patrimonio_netto: c.bilancio.patrimonio_netto,
          }),
        }),
      ]);
      const dataAdeguatezza = await adeguatezzaRes.json();
      if (!adeguatezzaRes.ok) setResultError(`Errore: ${JSON.stringify(dataAdeguatezza)}`);
      else setResult(dataAdeguatezza as CalcResult);

      const dataIndebitamento = await indebitamentoRes.json();
      if (!indebitamentoRes.ok) setResultIndebitamentoError(`Errore: ${JSON.stringify(dataIndebitamento)}`);
      else setResultIndebitamento(dataIndebitamento as CalcResult);
    } catch (e) {
      setResultError(e instanceof Error ? e.message : String(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { checkHealth(); }, []);
  useEffect(() => { runCalc(cliente); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [clienteId]);
  useEffect(() => {
    async function loadBalances() {
      if (!user) return;
      try {
        const r = await fetch("/api/balances/mine", {
          credentials: "include",
          headers: phaseAuthHeaders(),
        });
        if (!r.ok) return;
        const data = (await r.json()) as { items: UploadedBalanceApi[] };
        setSavedClienti(data.items.map(clienteFromUploadedBalance));
      } catch {
        // La persistenza non deve bloccare la demo.
      }
    }
    loadBalances();
  }, [user]);
  useEffect(() => {
    const timer = window.setTimeout(() => setShowImproveTab(true), 9000);
    return () => window.clearTimeout(timer);
  }, []);

  const semStyle = result ? semaforoStyle(result.semaforo) : null;
  const lettura = result ? letturaInterpretativa(cliente, result) : "";
  const semStyleIndebitamento = resultIndebitamento ? semaforoStyle(resultIndebitamento.semaforo) : null;
  const letturaIndebitamento = resultIndebitamento ? letturaInterpretativaIndebitamento(cliente, resultIndebitamento) : "";
  const defaultAssistantContext: AssistantContext = {
    kind: "general",
    clienteId: cliente.id,
    clienteName: cliente.ragione_sociale,
    contextType: "fixture_demo",
    contextRef: `cliente/${cliente.id}`,
    title: "Guida alla valutazione iniziale",
  };
  const activeAssistantContext = assistantContext ?? defaultAssistantContext;

  function openAssistant(context?: Partial<AssistantContext>) {
    setAssistantContext({ ...defaultAssistantContext, ...context });
    setAssistantOpen(true);
  }

  function updateDraft(key: keyof BilancioDraft, value: string) {
    setBilancioDraft((prev) => ({ ...prev, [key]: value }));
  }

  async function loadJsonFile(file: File | null) {
    if (!file) return;
    setUploadError(null);
    setUploadSourceName(file.name);
    if (!/\.json$/i.test(file.name) && file.type !== "application/json") {
      setUploadError("Questo caricatore genera il report solo da JSON strutturato o compilazione manuale. Il PDF non contiene ancora dati estraibili automaticamente: allegalo all'assistente come documento o inserisci qui i valori sintetici.");
      return;
    }
    try {
      const parsed = JSON.parse(await file.text()) as unknown;
      setBilancioDraft(draftFromJson(parsed));
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : String(e));
    }
  }

  async function generateUploadedReport() {
    setUploadError(null);
    try {
      const next = clienteFromDraft(bilancioDraft);
      if (user) {
        const r = await fetch("/api/balances", {
          method: "POST",
          headers: { "content-type": "application/json", ...phaseAuthHeaders() },
          credentials: "include",
          body: JSON.stringify(balancePayloadFromCliente(next, uploadSourceName)),
        });
        if (r.ok) {
          const saved = (await r.json()) as UploadedBalanceApi;
          const savedCliente = clienteFromUploadedBalance(saved);
          setSavedClienti((prev) => [savedCliente, ...prev.filter((item) => item.id !== savedCliente.id)]);
          setCustomCliente(null);
          setClienteId(savedCliente.id);
          setUploaderOpen(false);
          return;
        }
      }
      setCustomCliente(next);
      setClienteId(next.id);
      setUploaderOpen(false);
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : String(e));
    }
  }

  function downloadPdf() {
    window.print();
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 transition-colors">
      <header className="no-print border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 transition-colors">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <img src="/prora-logo.png" alt="PRORA" className="w-9 h-9 sm:w-10 sm:h-10 shrink-0" />
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold text-slate-900 dark:text-slate-100">PRORA — Tutela Preventiva</h1>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-0.5">
                Anteprima dimostrativa del documento di Valutazione Iniziale ai sensi dell'art. 2086 c.c. + art. 3 D.Lgs. 14/2019.
              </p>
            </div>
          </div>
          <div className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-start gap-2 sm:grid-cols-[auto_minmax(0,1fr)_auto] lg:w-auto lg:justify-end">
            <EsempiDropdown />
            <div className="min-w-0 text-left text-sm sm:text-right">
              {user ? (
                <>
                  <div className="truncate text-slate-700 dark:text-slate-300">{user.email}</div>
                  <div className="text-xs text-slate-600 dark:text-slate-400">{user.role === "admin" ? "amministrazione" : "esperto"}</div>
                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs sm:justify-end">
                    {user.role === "admin" && (
                      <a href="/admin" className="text-slate-700 dark:text-slate-300 underline">bacheca feedback</a>
                    )}
                    <button onClick={onSignOut} className="text-slate-700 dark:text-slate-300 underline">esci</button>
                  </div>
                </>
              ) : (
                <a href="/login" className="text-sm px-3 py-1.5 border border-slate-300 dark:border-slate-700 rounded text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800">
                  accedi
                </a>
              )}
            </div>
            <div className="row-start-1 col-start-2 sm:col-start-auto">
              <ThemeToggle />
            </div>
          </div>
        </div>
      </header>

      <main className="print-surface max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* Selettore cliente */}
        <section className="no-print bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-5 transition-colors">
          <div className="text-xs uppercase tracking-wider text-slate-600 dark:text-slate-400 mb-2">Cliente in valutazione</div>
          <div className="grid grid-cols-1 gap-2 sm:flex sm:flex-wrap">
            {clienti.map((c) => (
              <button
                key={c.id}
                onClick={() => setClienteId(c.id)}
                className={`px-3 py-1.5 rounded text-sm border transition-colors sm:w-auto ${
                  c.id === clienteId
                    ? "bg-slate-900 text-white border-slate-900 dark:bg-slate-100 dark:text-slate-900 dark:border-slate-100"
                    : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800"
                }`}
              >
                {c.ragione_sociale}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setUploaderOpen((value) => !value)}
              className="px-3 py-1.5 rounded text-sm border border-cyan-700 bg-cyan-950/40 text-cyan-50 hover:bg-cyan-900 transition-colors"
            >
              Nuovo bilancio
            </button>
          </div>
          <div className="text-xs text-slate-600 dark:text-slate-400 mt-2">
            Tre profili sintetici di micro impresa italiana (fascia ≤ 2 M ricavi). I dati di bilancio sono fittizi, costruiti per
            rappresentare situazioni patrimoniali tipiche (solida, in tensione moderata, sottocapitalizzata).
          </div>
          {uploaderOpen && (
            <div className="mt-5 rounded border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/60 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-medium text-slate-900 dark:text-slate-100">Carica o inserisci un bilancio sintetico</div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Puoi compilare i campi o caricare un JSON con `bilancio.totale_attivo`, `patrimonio_netto`, `ricavi`,
                    `ebitda`, `debiti_finanziari`, `risultato_esercizio`.
                  </p>
                </div>
                <input
                  type="file"
                  accept="application/json,.json"
                  onChange={(e) => loadJsonFile(e.target.files?.[0] ?? null)}
                  className="text-xs text-slate-600 dark:text-slate-300 file:mr-3 file:rounded file:border-0 file:bg-slate-900 file:px-3 file:py-1.5 file:text-white dark:file:bg-cyan-800"
                />
              </div>

              <div className="mt-4 grid md:grid-cols-3 gap-3">
                <input value={bilancioDraft.ragione_sociale} onChange={(e) => updateDraft("ragione_sociale", e.target.value)} placeholder="Ragione sociale" className="rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm text-slate-900 dark:text-slate-100" />
                <input value={bilancioDraft.settore_descrittivo} onChange={(e) => updateDraft("settore_descrittivo", e.target.value)} placeholder="Settore" className="rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm text-slate-900 dark:text-slate-100" />
                <input value={bilancioDraft.forma_giuridica} onChange={(e) => updateDraft("forma_giuridica", e.target.value)} placeholder="Forma giuridica" className="rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm text-slate-900 dark:text-slate-100" />
                <input value={bilancioDraft.esercizio} onChange={(e) => updateDraft("esercizio", e.target.value)} placeholder="Esercizio" className="rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm text-slate-900 dark:text-slate-100" />
                <input value={bilancioDraft.dipendenti} onChange={(e) => updateDraft("dipendenti", e.target.value)} placeholder="Dipendenti" className="rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm text-slate-900 dark:text-slate-100" />
                <input value={bilancioDraft.totale_attivo} onChange={(e) => updateDraft("totale_attivo", e.target.value)} placeholder="Totale attivo" className="rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm text-slate-900 dark:text-slate-100" />
                <input value={bilancioDraft.patrimonio_netto} onChange={(e) => updateDraft("patrimonio_netto", e.target.value)} placeholder="Patrimonio netto" className="rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm text-slate-900 dark:text-slate-100" />
                <input value={bilancioDraft.ricavi} onChange={(e) => updateDraft("ricavi", e.target.value)} placeholder="Ricavi" className="rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm text-slate-900 dark:text-slate-100" />
                <input value={bilancioDraft.ebitda} onChange={(e) => updateDraft("ebitda", e.target.value)} placeholder="EBITDA" className="rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm text-slate-900 dark:text-slate-100" />
                <input value={bilancioDraft.debiti_finanziari} onChange={(e) => updateDraft("debiti_finanziari", e.target.value)} placeholder="Debiti finanziari" className="rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm text-slate-900 dark:text-slate-100" />
                <input value={bilancioDraft.risultato_esercizio} onChange={(e) => updateDraft("risultato_esercizio", e.target.value)} placeholder="Risultato esercizio" className="rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm text-slate-900 dark:text-slate-100" />
              </div>

              {uploadError && <div className="mt-3 text-sm text-rose-700 dark:text-rose-300">{uploadError}</div>}
              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={generateUploadedReport}
                  className="px-4 py-2 rounded bg-slate-900 text-white dark:bg-cyan-800 hover:bg-slate-800 dark:hover:bg-cyan-700"
                >
                  Genera report
                </button>
              </div>
            </div>
          )}
        </section>

        {/* Scheda cliente + bilancio */}
        <section className="print-card bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-6 transition-colors">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4 mb-1">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">{cliente.ragione_sociale}</h2>
            <div className="flex items-center justify-between gap-3 sm:justify-end">
              <button
                type="button"
                onClick={downloadPdf}
                className="no-print min-h-9 text-xs px-3 py-1.5 rounded border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                Scarica PDF
              </button>
              <div className="text-sm text-slate-600 dark:text-slate-400">Esercizio {cliente.esercizio}</div>
            </div>
          </div>
          <div className="text-sm text-slate-600 dark:text-slate-400 mb-5">
            {cliente.forma_giuridica} · {cliente.settore_descrittivo} · {cliente.dipendenti} dipendenti
          </div>

          <div className="text-xs uppercase tracking-wider text-slate-600 dark:text-slate-400 mb-2">Dati di bilancio (sintesi)</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3 text-sm">
            <div className="flex justify-between border-b border-slate-100 dark:border-slate-800 pb-1">
              <span className="text-slate-600 dark:text-slate-400">Totale attivo</span>
              <span className="font-medium text-slate-900 dark:text-slate-100">{eur.format(cliente.bilancio.totale_attivo)}</span>
            </div>
            <div className="flex justify-between border-b border-slate-100 dark:border-slate-800 pb-1">
              <span className="text-slate-600 dark:text-slate-400">Patrimonio netto</span>
              <span className={`font-medium ${cliente.bilancio.patrimonio_netto < 0 ? "text-rose-700 dark:text-rose-400" : "text-slate-900 dark:text-slate-100"}`}>
                {eur.format(cliente.bilancio.patrimonio_netto)}
              </span>
            </div>
            <div className="flex justify-between border-b border-slate-100 dark:border-slate-800 pb-1">
              <span className="text-slate-600 dark:text-slate-400">Ricavi</span>
              <span className="font-medium text-slate-900 dark:text-slate-100">{eur.format(cliente.bilancio.ricavi)}</span>
            </div>
            <div className="flex justify-between border-b border-slate-100 dark:border-slate-800 pb-1">
              <span className="text-slate-600 dark:text-slate-400">EBITDA</span>
              <span className={`font-medium ${cliente.bilancio.ebitda < 0 ? "text-rose-700 dark:text-rose-400" : "text-slate-900 dark:text-slate-100"}`}>
                {eur.format(cliente.bilancio.ebitda)}
              </span>
            </div>
            <div className="flex justify-between border-b border-slate-100 dark:border-slate-800 pb-1">
              <span className="text-slate-600 dark:text-slate-400">Debiti finanziari</span>
              <span className="font-medium text-slate-900 dark:text-slate-100">{eur.format(cliente.bilancio.debiti_finanziari)}</span>
            </div>
            <div className="flex justify-between border-b border-slate-100 dark:border-slate-800 pb-1">
              <span className="text-slate-600 dark:text-slate-400">Risultato esercizio</span>
              <span className={`font-medium ${cliente.bilancio.risultato_esercizio < 0 ? "text-rose-700 dark:text-rose-400" : "text-slate-900 dark:text-slate-100"}`}>
                {eur.format(cliente.bilancio.risultato_esercizio)}
              </span>
            </div>
          </div>
        </section>

        {/* Indicatore Adeguatezza Patrimoniale */}
        <section className="print-card bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-6 transition-colors">
          <div className="flex items-baseline justify-between gap-4 mb-4">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Indicatore — Adeguatezza Patrimoniale</h2>
            <div className="text-xs text-slate-600 dark:text-slate-400">Riferimento: CNDCEC · art. 2086 c.c.</div>
          </div>

          {loading && <div className="text-sm text-slate-600 dark:text-slate-400">Calcolo in corso…</div>}

          {resultError && (
            <div className="text-sm text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 rounded p-3">
              {resultError}
            </div>
          )}

          {result && semStyle && (
            <>
              <div className={`border rounded-lg p-5 transition-colors ${semStyle.box}`}>
                <div className="flex items-baseline justify-between gap-4">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-600 dark:text-slate-400">
                      Patrimonio netto / Totale attivo
                    </div>
                    <div className={`text-4xl font-semibold mt-1 ${semStyle.label}`}>
                      {result.value !== null ? result.value.toFixed(4) : "—"}
                    </div>
                  </div>
                  <div className={`text-right ${semStyle.label}`}>
                    <div className="text-sm font-semibold uppercase tracking-wider">{result.semaforo}</div>
                    <div className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">soglia rif.: {result.threshold.toFixed(2)}</div>
                  </div>
                </div>
                <div className={`text-sm font-medium mt-3 ${semStyle.label}`}>{result.regola_applicata}</div>
                {result.flag_dati.length > 0 && (
                  <div className="text-xs text-slate-600 dark:text-slate-400 mt-2">
                    elementi rilevati: {result.flag_dati.join(" · ")}
                  </div>
                )}
              </div>

              <div className="mt-5">
                <div className="text-xs uppercase tracking-wider text-slate-600 dark:text-slate-400 mb-2">Lettura interpretativa</div>
                <p className="text-sm text-slate-800 dark:text-slate-200 leading-relaxed">{lettura}</p>
                <p className="text-xs text-slate-600 dark:text-slate-400 italic mt-3">
                  Testo dimostrativo generato da template fissi. La versione formale del documento è curata dal commercialista
                  ai sensi dell'art. 2086 c.c. e firmata dall'organo amministrativo.
                </p>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => openAssistant({
                    kind: "adeguatezza",
                    contextType: "blurb",
                    contextRef: `BLURB-002-ADEGUATEZZA-PATRIMONIALE/${cliente.id}`,
                    title: "Adeguatezza Patrimoniale",
                    value: result,
                  })}
                  className="text-xs px-3 py-1.5 rounded border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  Chiedi / lascia traccia su questo indicatore
                </button>
              </div>
            </>
          )}
        </section>

        {/* Indicatore Indebitamento Finanziario */}
        <section className="print-card bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-6 transition-colors">
          <div className="flex items-baseline justify-between gap-4 mb-4">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Indicatore — Indebitamento Finanziario</h2>
            <div className="text-xs text-slate-600 dark:text-slate-400">Riferimento: best practice finanza aziendale · soglie DEFAULT — DA VALIDARE</div>
          </div>

          {loading && <div className="text-sm text-slate-600 dark:text-slate-400">Calcolo in corso…</div>}

          {resultIndebitamentoError && (
            <div className="text-sm text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 rounded p-3">
              {resultIndebitamentoError}
            </div>
          )}

          {resultIndebitamento && semStyleIndebitamento && (
            <>
              <div className={`border rounded-lg p-5 transition-colors ${semStyleIndebitamento.box}`}>
                <div className="flex items-baseline justify-between gap-4">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-600 dark:text-slate-400">
                      Debiti finanziari / Patrimonio netto
                    </div>
                    <div className={`text-4xl font-semibold mt-1 ${semStyleIndebitamento.label}`}>
                      {resultIndebitamento.value !== null ? resultIndebitamento.value.toFixed(4) : "—"}
                    </div>
                  </div>
                  <div className={`text-right ${semStyleIndebitamento.label}`}>
                    <div className="text-sm font-semibold uppercase tracking-wider">{resultIndebitamento.semaforo}</div>
                    <div className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">soglia rif.: {resultIndebitamento.threshold.toFixed(2)}</div>
                  </div>
                </div>
                <div className={`text-sm font-medium mt-3 ${semStyleIndebitamento.label}`}>{resultIndebitamento.regola_applicata}</div>
                {resultIndebitamento.flag_dati.length > 0 && (
                  <div className="text-xs text-slate-600 dark:text-slate-400 mt-2">
                    elementi rilevati: {resultIndebitamento.flag_dati.join(" · ")}
                  </div>
                )}
              </div>

              <div className="mt-5">
                <div className="text-xs uppercase tracking-wider text-slate-600 dark:text-slate-400 mb-2">Lettura interpretativa</div>
                <p className="text-sm text-slate-800 dark:text-slate-200 leading-relaxed">{letturaIndebitamento}</p>
                <p className="text-xs text-amber-700 dark:text-amber-400 italic mt-3">
                  Soglie DEFAULT — DA VALIDARE. Non esiste fonte normativa cogente quantitativa per il rapporto
                  Debiti finanziari / PN nel quadro CCII vigente: le soglie qui usate (1 / 2 / 3) derivano da best
                  practice di finanza aziendale e sono da validare con il commercialista pilota.
                </p>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => openAssistant({
                    kind: "indebitamento",
                    contextType: "blurb",
                    contextRef: `BLURB-003-INDEBITAMENTO-FINANZIARIO/${cliente.id}`,
                    title: "Indebitamento Finanziario",
                    value: resultIndebitamento,
                  })}
                  className="text-xs px-3 py-1.5 rounded border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  Chiedi / lascia traccia su questo indicatore
                </button>
              </div>
            </>
          )}
        </section>

        <section className="print-card text-xs text-slate-500 dark:text-slate-400 leading-relaxed bg-slate-100 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded p-4 transition-colors">
          <strong className="text-slate-700 dark:text-slate-300">Disclaimer.</strong> Anteprima dimostrativa del prodotto in
          costruzione. Le soglie di riferimento sono attualmente impostate al valore CNDCEC generico e saranno calibrate sui
          macrosettori ATECO con il contributo del commercialista pilota. Nessuna delle valutazioni mostrate ha valore di
          parere professionale; la versione formale richiede approvazione esplicita del commercialista incaricato.
        </section>

        <footer className="no-print flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 pt-2">
          <div className="flex items-center gap-2">
            <span
              className={`inline-block w-2 h-2 rounded-full ${health?.ok ? "bg-emerald-500" : "bg-rose-500"}`}
              title={health?.ok ? "sistema operativo" : "sistema non disponibile"}
            />
            <span>sistema {health?.ok ? "operativo" : "non disponibile"}</span>
          </div>
          <div>PRORA — Tutela Preventiva · anteprima M1.5</div>
        </footer>
      </main>
      {showImproveTab && (
        <button
          type="button"
          onClick={() => openAssistant({
            kind: "general",
            contextType: "free",
            contextRef: `aiutaci-a-migliorare/${cliente.id}`,
            title: "Aiutaci a migliorare PRORA",
          })}
          className="no-print fixed left-0 top-1/2 z-30 -translate-y-1/2 rounded-r-lg border border-l-0 border-cyan-700 bg-cyan-950/90 px-3 py-4 text-xs font-medium uppercase tracking-wider text-cyan-50 shadow-lg hover:bg-cyan-900"
          style={{ writingMode: "vertical-rl", textOrientation: "mixed" }}
        >
          Aiutaci a migliorare
        </button>
      )}
      <ProraAssistant
        user={user}
        context={activeAssistantContext}
        open={assistantOpen}
        onOpen={openAssistant}
        onClose={() => setAssistantOpen(false)}
      />
    </div>
  );
}

// ------------------------------------------------------------
// App (routing path-based)
// ------------------------------------------------------------

export function App() {
  const [user, setUser] = useState<SessionUser>(null);
  const [loaded, setLoaded] = useState(false);
  const [path, setPath] = useState(window.location.pathname);

  useEffect(() => {
    fetchSession().then((u) => { setUser(u); setLoaded(true); });
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  async function handleSignOut() {
    await signOut();
    setUser(null);
    window.location.href = "/";
  }

  if (!loaded) return <div className="p-10 text-slate-600 dark:text-slate-400">…</div>;

  if (path === "/login") return <LoginPage />;
  if (path === "/admin") {
    if (!user) { window.location.href = "/login"; return null; }
    return <AdminBoardPage user={user} />;
  }
  return <HomePage user={user} onSignOut={handleSignOut} />;
}
